// Howl from Beyond deal-to-void helpers. Pure. No React, no Three.js.
//
// ── Codex (Howl from Beyond, Water Magic) ──
//   "Deal out your top spells, one to each void in the outer columns.
//    Draw any minions with Voidwalk or Monsters from among them and
//    banish the rest."
//
// "Outer columns" are the leftmost (col 0) and rightmost (col
// totalCols - 1) columns of the realm. A "void" there is a cell in
// those columns that holds no site (region === void). The spell deals
// one top-of-spellbook card to each such void, so the number of cards
// dealt equals the number of outer-column voids. Each dealt card is
// then DRAWN if it's a Minion with Voidwalk or a Monster, otherwise
// BANISHED.
//
// This module owns the two read-only computations the runtime needs:
//   • how many cards to deal (which outer-column cells are void)
//   • whether a dealt card is a keeper (draw) or banished
//
// The runtime reads the spellbook's top-N window, classifies each card
// against the live catalog row, then routes the keepers through the
// synced take-to-hand primitive and the rest through the synced
// banish-from-pile primitive.

/**
 * Outer-column void cells, as a Set of "col,row" keys. A cell counts as
 * a void when it carries no site (the `regionsAt` lookup reports 'void').
 *
 * @param totalCols / totalRows  grid dimensions
 * @param regionsAt  (col, row) => Set<region>. Site/region lookup
 *                   (regionsAtCell bound to the live mesh map)
 * @returns Set<string> of "col,row" keys
 */
export function outerColumnVoidCells(totalCols, totalRows, regionsAt) {
  const out = new Set();
  if (totalCols <= 0 || totalRows <= 0) return out;
  const cols = totalCols === 1 ? [0] : [0, totalCols - 1];
  for (const col of cols) {
    for (let row = 0; row < totalRows; row++) {
      const regions = regionsAt(col, row);
      if (regions && regions.has('void')) out.add(`${col},${row}`);
    }
  }
  return out;
}

/**
 * Classify a dealt spell against the "keep" rule. A keeper is a Minion
 * with Voidwalk OR a card whose subtypes include Monster; everything
 * else is banished.
 *
 * @param fullCard catalog row (type / traits / printedKeywords)
 * @returns 'draw' | 'banish'
 */
export function classifyDealtCard(fullCard) {
  if (!fullCard) return 'banish';
  const traits = (fullCard.traits || []).map((t) => String(t).toLowerCase());
  if (traits.includes('monster')) return 'draw';
  const keywords = (fullCard.printedKeywords || []).map((k) => String(k).toLowerCase());
  if (fullCard.type === 'Minion' && keywords.includes('voidwalk')) return 'draw';
  return 'banish';
}

/**
 * The top-N window of a spellbook, in pile order (top is the LAST
 * element, mirroring drawOp / blackMass). The spell deals to each void
 * from the top down; with fewer than `n` cards available it deals as
 * many as it can.
 *
 * @param cards spellbook pile cards (bottom → top)
 * @param n     number of outer-column voids
 * @returns the dealt cards, in top-to-bottom deal order
 */
export function dealtTopCards(cards, n) {
  if (!Array.isArray(cards) || n <= 0) return [];
  const slice = cards.slice(Math.max(0, cards.length - n));
  // Deal order is top-first; the window slice is bottom→top, so reverse.
  return slice.reverse();
}

// ── Cosmetic playback timing (shared host TTL ↔ client sequencer) ────
// Per-card stagger for the deal-in and the resolve-out, plus the reveal
// hold between them. Kept in the pure module so the host record TTL and
// the renderer sequencer can never drift.
export const HOWL_DEAL_MS = 320;
export const HOWL_HOLD_MS = 1100;
export const HOWL_RESOLVE_MS = 380;
const HOWL_TAIL_MS = 500; // last flight settle before teardown

// Wall-clock length of the full Howl reveal for n dealt cards. The host
// keeps the cosmetic record alive for this window before clearing it.
export function howlDurationMs(n) {
  const count = Math.max(0, n | 0);
  if (count === 0) return 0;
  return count * HOWL_DEAL_MS + HOWL_HOLD_MS + count * HOWL_RESOLVE_MS + HOWL_TAIL_MS;
}

/**
 * Build the ordered reveal plan for the cosmetic Howl playback. Pairs
 * the dealt cards (top-to-bottom deal order) with the outer-column voids
 * (sorted top-to-bottom: row first, then col) and classifies each so the
 * renderer knows where to fly the temporary face-up mesh and which aura
 * to attach. Pure. `cellCenter(col, row)` is injected so the module
 * stays free of Three.js / React.
 *
 * @param voids       Set<string> of "col,row" outer-column void keys
 * @param dealt       cards in deal order (top-to-bottom), from dealtTopCards
 * @param cellCenter  (col, row) => { x, z } world center of the void cell
 * @param getFullCard (cardId) => catalog row, for classification + face art
 * @returns ordered step array:
 *   { cardId, name, imageUrl, col, row, x, z,
 *     classification: 'draw'|'banish', accent: 'harbinger'|'warning' }
 */
export function planHowlDeal({ voids, dealt, cellCenter, getFullCard }) {
  const cells = [...(voids || [])]
    .map((key) => {
      const [col, row] = key.split(',').map(Number);
      return { col, row };
    })
    .sort((a, b) => a.row - b.row || a.col - b.col);

  const steps = [];
  const cards = Array.isArray(dealt) ? dealt : [];
  for (let i = 0; i < cards.length && i < cells.length; i++) {
    const card = cards[i];
    const { col, row } = cells[i];
    const center = cellCenter(col, row) || { x: 0, z: 0 };
    const full = getFullCard ? getFullCard(card.cardId) : null;
    const classification = classifyDealtCard(full);
    steps.push({
      cardId: card.cardId,
      name: full?.name || card.name || 'Card',
      imageUrl: full?.imageUrl || card.imageUrl || null,
      col,
      row,
      x: center.x,
      z: center.z,
      classification,
      accent: classification === 'draw' ? 'harbinger' : 'warning',
    });
  }
  return steps;
}
